//
// Vite entry point
//

import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
} from 'react-router-dom';
import './assets/css/index.css';
import 'react-tooltip/dist/react-tooltip.css';

import {
  AppTooltip,
  Banner,
} from './components';
import BanConditionOverlay from './components/BanConditionOverlay';
import ErrorBoundary from './components/ErrorBoundary';
import MainRoutes from './MainRoutes';
import BrowserProvider from './stores/browser/BrowserProvider';
import DisplayProvider from './stores/display/DisplayProvider';
import InitialAssetsProvider from './stores/townAssets/InitialAssetsProvider';
import WebAppProvider from './stores/webApp/WebAppProvider';
import {
  config,
  logDev,
  logError,
} from './utils';
import reportCrash from './utils/reportCrash';

// Global error handlers — catch unhandled errors before React mounts
window.addEventListener(
  'error',
  (event) => {
    reportCrash(event.error ?? event.message);
  },
);
window.addEventListener(
  'unhandledrejection',
  (event) => {
    reportCrash(event.reason);
  },
);

console.log(`dev=${config.dev}`);

const launchReact = (workerRegistration) => {
  logDev(`Main: launchReact with workerRegistration=${workerRegistration}`);

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <InitialAssetsProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <BrowserProvider>
              <WebAppProvider workerRegistration={workerRegistration}>
                <DisplayProvider>
                  <MainRoutes />

                  <AppTooltip />

                  {(config.dev && (!config.live))
                    ? <Banner corner text="dev" />
                    : null}

                  {(config.dev && config.live)
                    ? <Banner corner text="live dev" text2={config.liveUrlBase} />
                    : null}

                  {(config.demo)
                    ? <Banner corner text="demo" purple />
                    : null}

                  {(config.dev || config.demo)
                    ? <BanConditionOverlay />
                    : null}

                </DisplayProvider>
              </WebAppProvider>
            </BrowserProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </InitialAssetsProvider>
    </React.StrictMode>,
  );
};

let workerRegistration;

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register(
    '/service-worker.js',
    { scope: '/' },
  )
    .then((registration) => {
      workerRegistration = registration;

      launchReact(workerRegistration);
    })
    .catch((err) => {
      // SecurityError/TypeError mean a misdeployed worker script (404/MIME/parse) — the only actionable case; other rejections are reload, crawler or unsupported-context noise.
      const deployFailure = (err?.name === 'SecurityError') || (err?.name === 'TypeError');
      if (deployFailure) {
        logError(
          `Main: service Worker Registration Failed, err=${err}`,
          'Main.serviceWorkerRegistration.failed',
          {
            err: err?.message,
            ...(err?.name && { name: err.name }),
          },
        );
      } else {
        logDev(`Main: serviceWorker registration skipped, err=${err}`);
      }
      launchReact();
    });
} else {
  logDev('Main: serviceWorker service not available');
  launchReact();
}
